import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Clock, XCircle } from 'react-feather';
import { updateAuth } from '../store/slices/authSlice';
import { userService } from '../services/userService';
import PaymentService from '../services/paymentService';

const PAYMENT_STATUS = {
  checking: 'checking',
  confirmed: 'confirmed',
  pending: 'pending',
  error: 'error'
};

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authState = useSelector(state => state.auth);
  const [status, setStatus] = useState(PAYMENT_STATUS.checking);

  const initialSubscriptionStateRef = useRef({
    packageEndDate: authState.packageEndDate || null,
    level: authState.level || null,
    invitations: authState.invitations || 0
  });

  useEffect(() => {
    let cancelled = false;

    const hasAccountUpdate = (userData) => (
      userData?.packageEndDate !== initialSubscriptionStateRef.current.packageEndDate ||
      userData?.level !== initialSubscriptionStateRef.current.level ||
      (userData?.invitations || 0) !== initialSubscriptionStateRef.current.invitations
    );

    const refreshPaymentStatus = async () => {
      if (!authState.uid) {
        setStatus(PAYMENT_STATUS.error);
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const chariowReference = params.get('paymentRef') || params.get('ref') || params.get('reference');

      if (chariowReference) {
        for (let attempt = 0; attempt < 6; attempt += 1) {
          try {
            const paymentStatus = await PaymentService.verifyChariowPayment(chariowReference);
            if (cancelled) return;

            if (paymentStatus.status === 'completed') {
              const userData = await userService.getUserByFbid(authState.uid);
              if (cancelled) return;

              if (userData) {
                dispatch(updateAuth(userData));
              }

              setStatus(PAYMENT_STATUS.confirmed);
              window.history.replaceState({}, '', '/payment/success');
              return;
            }

            if (paymentStatus.status === 'failed' || paymentStatus.status === 'not_found') {
              setStatus(PAYMENT_STATUS.error);
              return;
            }
          } catch (error) {
            console.error('Erreur lors de la verification directe Chariow:', error);
            if (!cancelled) setStatus(PAYMENT_STATUS.error);
            return;
          }

          await new Promise(resolve => setTimeout(resolve, 2500));
        }

        if (!cancelled) setStatus(PAYMENT_STATUS.pending);
        return;
      }

      for (let attempt = 0; attempt < 6; attempt += 1) {
        try {
          const userData = await userService.getUserByFbid(authState.uid);
          if (cancelled) return;

          if (userData) {
            dispatch(updateAuth(userData));

            if (hasAccountUpdate(userData)) {
              setStatus(PAYMENT_STATUS.confirmed);
              return;
            }
          }
        } catch (error) {
          console.error('Erreur lors de la verification du paiement Chariow:', error);
          if (!cancelled) setStatus(PAYMENT_STATUS.error);
          return;
        }

        await new Promise(resolve => setTimeout(resolve, 2500));
      }

      if (!cancelled) setStatus(PAYMENT_STATUS.pending);
    };

    refreshPaymentStatus();

    return () => {
      cancelled = true;
    };
  }, [authState.uid, dispatch]);

  const view = {
    [PAYMENT_STATUS.checking]: {
      icon: <Clock size={44} />,
      title: 'Confirmation du paiement',
      text: 'Nous verifions votre paiement. Cette etape peut prendre quelques secondes.'
    },
    [PAYMENT_STATUS.confirmed]: {
      icon: <CheckCircle size={44} />,
      title: 'Paiement confirme',
      text: 'Votre compte a ete mis a jour.'
    },
    [PAYMENT_STATUS.pending]: {
      icon: <Clock size={44} />,
      title: 'Paiement en cours de confirmation',
      text: 'Le paiement a bien été transmis, mais sa confirmation finale peut prendre quelques instants.'
    },
    [PAYMENT_STATUS.error]: {
      icon: <XCircle size={44} />,
      title: 'Verification indisponible',
      text: 'Impossible de verifier le paiement pour le moment. Vous pouvez reessayer depuis votre profil.'
    }
  }[status];

  return (
    <div className="content-body page-wrapper">
      <div className="container py-5">
        <div className="d-flex flex-column align-items-center text-center mx-auto" style={{ maxWidth: 420 }}>
          <div className="mb-4" style={{ color: status === PAYMENT_STATUS.error ? '#dc3545' : '#FF50A2' }}>
            {view.icon}
          </div>
          <h4 className="mb-3">{view.title}</h4>
          <p className="text-muted mb-4">{view.text}</p>
          <button className="btn btn-primary w-100 mb-2" onClick={() => navigate('/profile', { replace: true })}>
            Voir mon profil
          </button>
          <button className="btn btn-outline-secondary w-100" onClick={() => navigate('/subscription', { replace: true })}>
            Retour aux abonnements
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
